import {
  Button,
  Chip,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  ScrollableContent,
  ScrollableFooter,
  selectColumn,
  simpleHeader,
  SimpleHeaderAndData,
  Tag,
  useComputed,
  useGridTableApi,
  useSnackbar,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { priceCell, SearchBox } from "src/components";
import { CommitmentStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { TableActions } from "src/routes/layout/TableActions";
import { ProjectParams } from "src/routes/routesDef";
import { createCommitmentsUrl, createReleaseToTradesReviewUrl } from "src/RouteUrls";
import { formatList, pluralize, queryResult } from "src/utils";
import { commitmentStatusTagTypeMapper } from "src/utils/mappers";
import {
  ConfirmTradesStepV2_ProjectFragment,
  ConfirmTradesStepV2_RelevantProjectBidPackageFragment,
  useAssignBidPackageWorkToBidContractMutation,
  useConfirmTradesStepV2Query,
} from "./ConfirmTradesStepV2.generated";

export function ConfirmTradesStepV2() {
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useConfirmTradesStepV2Query({ variables: { id: projectId } });
  return queryResult(query, ({ project, relevantProjectBidPackages }) => (
    <ConfirmTradesStepV2View
      project={project}
      projectId={projectId}
      relevantProjectBidPackages={relevantProjectBidPackages}
    />
  ));
}

type Row = SimpleHeaderAndData<ConfirmTradesStepV2_RelevantProjectBidPackageFragment>;

type ConfirmTradesStepV2ViewProps = {
  project: ConfirmTradesStepV2_ProjectFragment;
  projectId: string;
  relevantProjectBidPackages: ConfirmTradesStepV2_RelevantProjectBidPackageFragment[];
};

function ConfirmTradesStepV2View(props: ConfirmTradesStepV2ViewProps) {
  const { project, projectId, relevantProjectBidPackages } = props;
  const navigate = useNavigate();
  const tableApi = useGridTableApi<Row>();
  const [search, setSearch] = useState("");
  const columns = useMemo(() => createColumns(projectId), [projectId]);
  const rows = useMemo(() => createRows(relevantProjectBidPackages), [relevantProjectBidPackages]);
  const [assignBidPackageWorkToBidContract, { loading }] = useAssignBidPackageWorkToBidContractMutation();
  const selectedRows = useComputed(() => tableApi.getSelectedRows("data"), [tableApi]);
  const { triggerNotice } = useSnackbar();

  const createDisabled = loading
    ? true
    : selectedRows.isEmpty
      ? "Select at least one bid package"
      : selectedRows.some((r) => r.data.preferredBidContractRevision.bidContract.isInternalEstimate)
        ? "Commitments cannot be created from internal estimates"
        : false;

  async function createPurchaseOrders() {
    const bidContractRevisionIds = selectedRows.map((r) => r.data.preferredBidContractRevision.id);
    const result = await assignBidPackageWorkToBidContract({
      variables: { input: { projectId, bidContractRevisionIds } },
    });
    const createdCommitments = result.data?.assignBidPackageWorkToBidContract ?? [];
    triggerNotice({
      icon: "success",
      message: `${createdCommitments.length} draft purchase ${pluralize(createdCommitments, "order")} created`,
    });
    navigate(createReleaseToTradesReviewUrl(projectId));
  }

  return (
    <>
      <div css={Css.px8.pt3.$}>
        <div css={Css.mb2.$}>
          <span css={Css.mdSb.$}>Select bid packages and corresponding trades to create a purchase order.</span>
        </div>
        <TableActions>
          <span css={Css.lg.fw6.$}>{project.readyPlanConfig?.readyPlan?.displayName ?? ""}</span>
          <SearchBox onSearch={setSearch} updateQueryString={false} />
        </TableActions>
        <ScrollableContent virtualized>
          <div css={Css.px7.$}>
            <GridTable
              api={tableApi}
              columns={columns}
              rows={rows}
              filter={search}
              as="virtual"
              stickyHeader
              sorting={{ on: "client" }}
              fallbackMessage="No line items found."
            />
          </div>
        </ScrollableContent>
      </div>
      <ScrollableFooter>
        <div css={Css.df.px8.py3.bshHover.bgWhite.$}>
          <div css={Css.mla.df.gap2.$}>
            <Button
              size="lg"
              variant="quaternary"
              label="Exit"
              onClick={() => navigate(createCommitmentsUrl(project.id))}
            />
            <Button size="lg" disabled={createDisabled} label="Create" onClick={createPurchaseOrders} />
          </div>
        </div>
      </ScrollableFooter>
    </>
  );
}

function createRows(
  relevantProjectBidPackages: ConfirmTradesStepV2_RelevantProjectBidPackageFragment[],
): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...relevantProjectBidPackages.map(
      (data): GridDataRow<Row> => ({
        kind: "data",
        id: data.id,
        data,
        // Internal estimates can't be turned into purchase orders, so their rows aren't selectable.
        selectable: data.preferredBidContractRevision.bidContract.isInternalEstimate ? false : undefined,
      }),
    ),
  ];
}

function createColumns(projectId: string): GridColumn<Row>[] {
  return [
    selectColumn({ w: 0.1 }),
    column<Row>({
      header: "Cost Code",
      data: ({ bidPackage }) => formatList(bidPackage.costCodes.map((c) => c.displayName)),
    }),
    column<Row>({ header: "Bid Type", data: ({ bidPackage }) => (bidPackage.isUnitBased ? "Unit Bid" : "Plan Bid") }),
    column<Row>({ header: "Cost Type", data: ({ bidPackage }) => bidPackage.costType.name }),
    numericColumn<Row>({
      header: "Total Cost",
      data: ({ totalCostInCents }) => priceCell({ valueInCents: totalCostInCents }),
    }),
    // Empty column to give the cost column some breathing room since it's auto-aligned to the right
    column<Row>({ header: emptyCell, data: emptyCell, clientSideSort: false }),
    column<Row>({
      header: "Trade",
      data: ({ preferredBidContractRevision }) => {
        const { bidContract } = preferredBidContractRevision;
        if (bidContract.isInternalEstimate) {
          return { value: "Internal Estimate", content: () => <Tag text="INTERNAL ESTIMATE" type="update" /> };
        }
        return bidContract.bidder.name;
      },
    }),
    column<Row>({
      header: "Existing POs",
      data: ({ existingCommitments }) => {
        const commitmentLikes = existingCommitments.filter(
          (c) => c.project.id === projectId && c.status !== CommitmentStatus.Voided,
        );
        if (commitmentLikes.isEmpty) return emptyCell;
        return {
          alignment: "left" as const,
          value: commitmentLikes.map((c) => c.shortName).join(),
          content: () => (
            <div css={Css.df.fdc.gap1.$}>
              {commitmentLikes.map((c) => (
                <div key={c.id} css={Css.df.aic.gap1.$}>
                  <Link to={c.blueprintUrl.path} target="_blank">
                    <Chip text={c.shortName} compact />
                  </Link>
                  <Tag type={commitmentStatusTagTypeMapper(c.status, c.statusText)} text={c.statusText} />
                </div>
              ))}
            </div>
          ),
        };
      },
      clientSideSort: false,
    }),
  ];
}
